/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import highlightWords from 'highlight-words';

const DEFAULT_TRUNCATION_LENGTH = 40;

interface HighlightedTextArgs {
  text: string;
  highlightClass?: string;
  query?: string;
  truncate?: boolean;
  truncationLength?: number;
}

export default class HighlightedText extends Component<HighlightedTextArgs> {
  get truncate() {
    return this.args.truncate ?? false;
  }

  get query() {
    return this.args.query ?? '';
  }

  get truncationLength() {
    return this.args.truncationLength ?? DEFAULT_TRUNCATION_LENGTH;
  }

  get textNeedsTruncation() {
    return this.truncate && this.args.text.length > this.truncationLength;
  }

  get truncatedText() {
    // Creates a string that is either truncated at its beginning, middle, or end depending on where the query string occurs in the text.

    if (!this.textNeedsTruncation) {
      return this.args.text;
    }

    let queryLength = this.query.length;

    if (queryLength === 0) {
      return `${this.args.text.substring(0, this.truncationLength)}…`;
    }

    let indexOfQueryMatch = this.args.text.indexOf(this.query);
    let bufferLength = Math.floor((this.truncationLength - queryLength) / 2);

    if (indexOfQueryMatch === -1 || indexOfQueryMatch === 0 || indexOfQueryMatch < bufferLength) {
      // if query is not present in the text, is at the beginning of the text or is within the buffer, truncate the text at the beginning
      return `${this.args.text.substring(0, this.truncationLength)}…`;
    } else if (indexOfQueryMatch + queryLength >= this.args.text.length - bufferLength) {
      // if the query is present at the end of the text inclusive of the buffer, truncate the text at the end
      let start = indexOfQueryMatch - bufferLength;
      let end = this.args.text.length;

      return `…${this.args.text.substring(start, end)}`;
    } else {
      // if the query is present in the middle of the text, truncate the text at the middle
      let start = indexOfQueryMatch - bufferLength;
      let end = indexOfQueryMatch + queryLength + bufferLength;

      return `…${this.args.text.substring(start, end)}…`;
    }
  }

  get highlightedWords() {
    if (typeof this.query !== 'string') {
      return undefined;
    }

    let { text } = this.args;

    if (this.truncate) {
      text = this.truncatedText;
    }

    return highlightWords({
      text,
      query: this.query,
    });
  }
}
